/**
 * Potrivește un credential importat pe schema tipului din spațiul de
 * destinație — aceeași împărțire ca la crearea din formular
 * (`partitionValues`): atributele secrete intră în blob-ul criptat, cele
 * non-secrete în `meta`.
 *
 * Schema destinației decide, nu fișierul: un câmp pe care spațiul îl ține
 * secret ajunge criptat chiar dacă în sursă era în clar, iar orice cheie pe
 * care schema n-o cunoaște merge tot în blob-ul criptat — nimic necunoscut nu
 * ajunge în clar la server.
 */
import type { CredentialTypeSchemaResponse } from '@/api/credentialSchemas';
import type { CredentialType, MetaValue } from '@/api/credentials';
import type { CredentialPayload } from '@/crypto/sodium';
import { TransferError, type TransferCredential } from '@/features/vault/transfer/types';

// Limitele backend-ului (schemas/credentials.py, api/credentials.py).
const TITLE_MAX = 256;
const USERNAME_MAX = 256;
const META_VALUE_MAX = 2048;
const BLOB_MAX_BYTES = 2 * 1024 * 1024;
const SECRETBOX_OVERHEAD = 16;

export interface PreparedCredential {
  type: CredentialType;
  title: string;
  url?: string;
  username?: string;
  expires_at?: string;
  meta: Record<string, MetaValue>;
  /** De criptat — nu pleacă niciodată în clar. */
  payload: CredentialPayload;
}

function isEmpty(value: unknown): boolean {
  return value === undefined || value === null || (typeof value === 'string' && !value.trim());
}

export function prepareCredential(
  item: TransferCredential,
  schema: CredentialTypeSchemaResponse | undefined,
): PreparedCredential {
  if (!schema) throw new TransferError('schema_missing');
  const title = item.title.trim().slice(0, TITLE_MAX);
  if (!title) throw new TransferError('missing_title');

  const fields: Record<string, unknown> = { ...item.meta, ...item.payload };
  if (isEmpty(fields.url) && item.url) fields.url = item.url;
  if (isEmpty(fields.username) && item.username) fields.username = item.username;

  const meta: Record<string, MetaValue> = {};
  const payload: CredentialPayload = {};
  const secretKeys = new Set<string>();
  const known = new Set<string>();

  for (const attr of schema.attributes) {
    known.add(attr.key);
    if (attr.secret) secretKeys.add(attr.key);
    const value = fields[attr.key];
    if (isEmpty(value)) {
      if (attr.required) throw new TransferError('missing_required', { field: attr.label });
      continue;
    }
    if (attr.secret) {
      payload[attr.key] = value;
    } else if (attr.type === 'number') {
      const n = Number(value);
      if (Number.isNaN(n)) throw new TransferError('invalid_number', { field: attr.label });
      meta[attr.key] = n;
    } else {
      const str = String(value);
      if (str.length > META_VALUE_MAX) {
        throw new TransferError('value_too_long', { field: attr.label });
      }
      meta[attr.key] = str;
    }
  }
  for (const [key, value] of Object.entries(fields)) {
    if (!known.has(key) && !isEmpty(value)) payload[key] = value;
  }

  const size = new TextEncoder().encode(JSON.stringify(payload)).length + SECRETBOX_OVERHEAD;
  if (size > BLOB_MAX_BYTES) throw new TransferError('too_large');

  const prepared: PreparedCredential = { type: item.type, title, meta, payload };
  // Coloanele `url` / `username` sunt vizibile serverului: le trimitem doar
  // dacă schema spațiului nu le-a declarat secrete.
  const url = typeof fields.url === 'string' ? fields.url.trim() : '';
  if (url && url.length <= META_VALUE_MAX && !secretKeys.has('url')) prepared.url = url;
  const username = typeof fields.username === 'string' ? fields.username.trim() : '';
  if (username && username.length <= USERNAME_MAX && !secretKeys.has('username')) {
    prepared.username = username;
  }
  if (item.expires_at && !Number.isNaN(new Date(item.expires_at).getTime())) {
    prepared.expires_at = new Date(item.expires_at).toISOString();
  }
  return prepared;
}

/**
 * Cheia după care două credentiale sunt considerate același la import: tip,
 * titlu, url, username și atributele non-secrete. Conținutul secret nu intră
 * în comparație — ar însemna decriptarea întregului vault.
 */
export function duplicateKey(c: {
  type: string;
  title: string;
  url?: string | null;
  username?: string | null;
  meta: Record<string, MetaValue>;
}): string {
  const meta = Object.entries(c.meta)
    .filter(([, value]) => value !== null && value !== '')
    .map(([key, value]) => `${key}=${String(value)}`)
    .sort();
  return [c.type, c.title.trim(), c.url ?? '', c.username ?? '', ...meta]
    .map((part) => part.toLowerCase())
    .join('\u0000');
}
